/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { Button, Page } from './uiold';
import LoadingIndicator from './uiold/LoadingIndicator';
import connectData, { ConnectedProps } from '../support/connectData';
import { DEFAULT_FORMS } from '../support/shared/constants/Constants';
import FormBuilderActions from '../support/FormBuilderActions';
import FieldsEditor from './FormBuilder/FieldsEditor';
import SignupFormModel from './FormBuilder/SignupFormModel';
import FormFieldDialog from './FormBuilder/FormFieldDialog';
import createFormElement from './FormBuilder/FormElements/helpers/createFormElement';
import { FormContext } from './Embed/FormEmbed';
import { canUser } from '../support/shared/permissionHelpers';
import NoPermissionMessage from './Users/WritePermissionMessage';
import ExampleLink from './ExampleLink';

import { User } from '../types/shared/User';
import { Group } from '../types/shared/Group';
import { FormElementType } from '../types/shared/FormFieldDefinition';

type Props = {
  formId: string;
};

function FormBuilder(props: FullProps) {
  let {
    formDef,
    account,
    season,
    isLoading,
    markets,
    isDirty,
    isSaving,
    saveFormDef,
    fetchAll,
    user,
    addons,
  } = props;

  let canEdit = canUser(user, { formBuilder: 'WRITE' });

  let [isAddingGroup, setIsAddingGroup] = useState(false);
  let [isAddFieldDialogOpen, setIsAddFieldDialogOpen] = useState(false);
  let [addBelowFieldID, setAddBelowFieldID] = useState<string | null>(null);
  let fieldsEditor = useRef<FieldsEditor>(null);

  let defaultFormName = DEFAULT_FORMS.signup.name;
  let accountCurrency =
    (account && account.settings && account.settings.currency) || 'USD';

  let setSelectedField = (selectedFieldID: string | null) => {
    // eslint-disable-next-line no-underscore-dangle
    fieldsEditor.current?._setSelectedField(selectedFieldID);
  };

  let onCancel = () => {
    setSelectedField(null);
    setIsAddFieldDialogOpen(false);
  };

  let onFieldPress = (type: FormElementType) => {
    let formField = createFormElement((formDef && formDef.id) || '', type);
    if (formField && typeof formField.id === 'string') {
      setSelectedField(formField.id);
    }
    if (formField && addBelowFieldID) {
      FormBuilderActions.addFieldBelow(formField, addBelowFieldID);
    }
    setIsAddFieldDialogOpen(false);
  };

  let onCreateGroupStart = () => {
    setIsAddingGroup(true);
  };

  let onCreateGroupCancel = () => {
    setIsAddingGroup(false);
  };

  let onSaveGroup = (group: Group) => {
    FormBuilderActions.saveFieldGroup(group);
    setIsAddingGroup(false);
  };

  let onRemoveGroup = (group: Group) => {
    FormBuilderActions.removeFieldGroup(group);
  };

  let openAddFieldDialog = (fieldID: string) => {
    setIsAddFieldDialogOpen(true);
    setAddBelowFieldID(fieldID);
  };

  return (
    <FormContext.Provider value={{ accountCurrency }}>
      <Page isLoading={!formDef || !account || !markets || isLoading}>
        <Page.Header title={(formDef && formDef.name) || defaultFormName}>
          <View style={styles.buttonToolbar}>
            <SaveControls
              isDirty={isDirty}
              isSaving={isSaving}
              fetchAll={fetchAll}
              isAddingGroup={isAddingGroup}
              onSave={saveFormDef}
              user={user}
            />
          </View>
        </Page.Header>
        {formDef && markets && account && canEdit ? (
          <FieldsEditor
            ref={fieldsEditor}
            formDef={formDef as SignupFormModel}
            markets={markets}
            onAddFieldPress={openAddFieldDialog}
            isAddingGroup={isAddingGroup}
            onSaveGroup={onSaveGroup}
            onRemoveGroup={onRemoveGroup}
            onEditGroupCancel={onCreateGroupCancel}
            onCreateGroupStart={onCreateGroupStart}
            accountID={account.id}
            seasonID={season?.id}
            addons={addons}
          />
        ) : (
          account && (
            <ExampleLink
              resourceID="forms/signup"
              accountID={account.id}
              seasonID={season?.id}
            />
          )
        )}
        {isAddFieldDialogOpen && (
          <FormFieldDialog onCancel={onCancel} onSelect={onFieldPress} />
        )}
      </Page>
    </FormContext.Provider>
  );
}

function SaveControls(props: {
  isDirty: boolean;
  isSaving: boolean;
  fetchAll: (...args: Array<any>) => any;
  isAddingGroup: boolean;
  onSave: (...args: Array<any>) => any;
  user: User | null | undefined;
}) {
  let { isDirty, isSaving, fetchAll, isAddingGroup, onSave, user } = props;
  if (isAddingGroup || !isDirty) {
    return null;
  }
  if (isSaving) {
    return <LoadingIndicator />;
  }
  return canUser(user, { formBuilder: 'WRITE' }) ? (
    <>
      <Button onPress={fetchAll} title={t('Revert')} />
      <Button onPress={onSave} isPrimary title={t('Save Changes')} />
    </>
  ) : (
    <NoPermissionMessage />
  );
}

let styles = StyleSheet.create({
  buttonToolbar: {
    flexDirection: 'row',
  },
});

const connector = connectData<Props>()(
  () => ({
    formDef: {
      dataStoreKey: 'forms/:id',
      params: ['signup'],
    },
    account: {
      dataStoreKey: 'account',
    },
    addons: { dataStoreKey: 'addons' },
    season: {
      dataStoreKey: 'season',
    },
    markets: {
      dataStoreKey: 'markets',
    },
    user: {
      dataStoreKey: 'user',
    },
  }),
  () => ({
    saveFormDef: {
      dataStoreKey: 'forms/:id',
      params: ['signup'],
    },
  }),
);
type FullProps = ConnectedProps<typeof connector>;
export default connector(FormBuilder);
